/* ==========================================================================
   YouTube Lazy & Privacy-First Embed Component
   Privacy-by-Design & GDPR Compliant: No upfront connection or iframe loaded before user consent
   Supports youtube-nocookie.com, thumbnail preview & "Povolit externí média"
   ========================================================================== */

import { getLocale } from '../locales/i18n.js';
import { hasConsent, updateConsentState, getSavedConsent } from './CookieBanner.js';

const ytText = {
  cs: {
    enableBtn: 'Povolit externí média a přehrát video',
    privacyNote: 'Přehráním videa načtete obsah ze služby YouTube (Google LLC) v režimu zvýšené ochrany soukromí.',
    cookieSettings: 'Nastavení cookies'
  },
  en: {
    enableBtn: 'Enable external media and play video',
    privacyNote: 'Playing this video loads content from YouTube (Google LLC) in privacy-enhanced mode.',
    cookieSettings: 'Cookie Settings'
  },
  de: {
    enableBtn: 'Externe Medien aktivieren und Video abspielen',
    privacyNote: 'Durch das Abspielen dieses Videos werden Inhalte von YouTube (Google LLC) geladen.',
    cookieSettings: 'Cookie-Einstellungen'
  },
  es: {
    enableBtn: 'Permitir medios externos y reproducir video',
    privacyNote: 'Al reproducir este video, se carga contenido de YouTube (Google LLC) en modo de privacidad mejorada.',
    cookieSettings: 'Configuración de cookies'
  },
  it: {
    enableBtn: 'Consenti media esterni e riproduci video',
    privacyNote: 'La riproduzione di questo video carica contenuti da YouTube (Google LLC) in modalità di privacy avanzata.',
    cookieSettings: 'Impostazioni cookie'
  },
  pt: {
    enableBtn: 'Permitir mídia externa e reproduzir vídeo',
    privacyNote: 'A reprodução deste vídeo carrega conteúdo do YouTube (Google LLC) em modo de privacidade aprimorada.',
    cookieSettings: 'Configurações de cookies'
  },
  ar: {
    enableBtn: 'السماح بالوسائط الخارجية وتشغيل الفيديو',
    privacyNote: 'تشغيل هذا الفيديو يقوم بتحميل محتوى من YouTube (Google LLC) بوضع الخصوصية المحسنة.',
    cookieSettings: 'إعدادات ملفات تعريف الارتباط'
  }
};

/**
 * Extract clean 11-char YouTube Video ID from any URL or raw ID
 */
export function extractYouTubeId(urlOrId) {
  if (!urlOrId) return '';
  const str = String(urlOrId).trim();
  if (str.length === 11 && !str.includes('/') && !str.includes('?')) return str;
  if (str.includes('youtu.be/')) {
    return str.split('youtu.be/')[1].split('?')[0].split('&')[0];
  }
  if (str.includes('watch?v=')) {
    return str.split('watch?v=')[1].split('&')[0];
  }
  if (str.includes('embed/')) {
    return str.split('embed/')[1].split('?')[0].split('&')[0];
  }
  return str;
}

/**
 * Create a privacy-first YouTube embed container
 */
export function createYouTubeEmbed(optionsOrId = {}, maybeTitle) {
  let videoId = '';
  let title = 'YouTube Video';
  let poster = '';
  let aspectRatio = '56.25%';
  let borderRadius = 'var(--radius-unified, 12px)';

  if (typeof optionsOrId === 'string') {
    videoId = optionsOrId;
    if (maybeTitle) title = maybeTitle;
  } else if (optionsOrId && typeof optionsOrId === 'object') {
    videoId = optionsOrId.videoId || '';
    if (optionsOrId.title) title = optionsOrId.title;
    if (optionsOrId.poster) poster = optionsOrId.poster;
    if (optionsOrId.aspectRatio) aspectRatio = optionsOrId.aspectRatio;
    if (optionsOrId.borderRadius) borderRadius = optionsOrId.borderRadius;
  }

  let userTriggeredPlay = Boolean(optionsOrId && typeof optionsOrId === 'object' && optionsOrId.autoplay);

  const cleanId = extractYouTubeId(videoId);
  const wrapper = document.createElement('div');
  wrapper.className = 'sed-youtube-embed-wrapper';
  wrapper.style.cssText = `
    position: relative;
    padding-bottom: ${aspectRatio};
    height: 0;
    overflow: hidden;
    border-radius: ${borderRadius};
    background-color: var(--navy-dark, #0a192f);
    border: 1px solid var(--slate-line, rgba(255,255,255,0.12));
    box-shadow: 0 4px 15px rgba(0,0,0,0.08);
  `;

  function renderContent() {
    const isGranted = hasConsent('externalMedia');
    const lang = getLocale() || 'en';
    const t = ytText[lang] || ytText.en;
    const isRtl = lang === 'ar';

    if (isGranted) {
      const autoplayParam = userTriggeredPlay ? 'autoplay=1&' : '';
      wrapper.innerHTML = `
        <iframe
          src="https://www.youtube-nocookie.com/embed/${cleanId}?${autoplayParam}rel=0"
          title="${title}"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerpolicy="strict-origin-when-cross-origin"
          allowfullscreen
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0;"
        ></iframe>
      `;
    } else {
      // Lazy placeholder with no upfront connection to google
      const bgStyle = poster 
        ? `background: linear-gradient(rgba(10,25,47,0.7), rgba(10,25,47,0.85)), url('${poster}') center/cover no-repeat;`
        : `background: linear-gradient(135deg, #07192f 0%, #1e3a8a 100%);`;

      wrapper.innerHTML = `
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          ${bgStyle}
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 1.5rem;
          box-sizing: border-box;
          color: #ffffff;
          direction: ${isRtl ? 'rtl' : 'ltr'};
        ">
          <!-- Play Icon Preview -->
          <div style="
            width: 58px;
            height: 58px;
            background: rgba(53, 168, 223, 0.95);
            color: #ffffff;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            margin-bottom: 1rem;
            box-shadow: 0 8px 25px rgba(0,0,0,0.4);
            cursor: pointer;
            transition: transform 0.2s;
          " class="sed-yt-play-trigger" title="${t.enableBtn}">
            ▶
          </div>

          <div style="font-weight: 800; font-size: 1.05rem; margin-bottom: 0.5rem; max-width: 520px; line-height: 1.35;">
            ${title}
          </div>

          <p style="font-size: 0.8rem; color: rgba(255,255,255,0.75); max-width: 440px; margin: 0 0 1.25rem; line-height: 1.45;">
            ${t.privacyNote}
          </p>

          <button class="sed-btn sed-btn-primary sed-yt-enable-btn" style="font-size: 0.88rem; font-weight: 800; padding: 0.55rem 1.25rem; border-radius: 8px; cursor: pointer; box-shadow: 0 4px 15px rgba(53, 168, 223, 0.4);">
            ▶ ${t.enableBtn}
          </button>
        </div>
      `;

      const unlockMedia = () => {
        userTriggeredPlay = true;
        const cur = getSavedConsent() || { necessary: true, analytics: false, externalMedia: false };
        updateConsentState({
          analytics: Boolean(cur.analytics),
          externalMedia: true
        });
      };

      wrapper.querySelector('.sed-yt-enable-btn')?.addEventListener('click', (e) => {
        e.preventDefault();
        unlockMedia();
      });

      wrapper.querySelector('.sed-yt-play-trigger')?.addEventListener('click', (e) => {
        e.preventDefault();
        unlockMedia();
      });
    }
  }

  // Listen for consent changes across the application
  const onConsentChanged = () => renderContent();
  window.addEventListener('sed:consentChange', onConsentChanged);

  // Clean up listener when wrapper is removed from DOM if MutationObserver is available
  renderContent();
  return wrapper;
}
